import axios from "axios";
import uniqid from "uniqid";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Persistent per-browser visitor id, used to dedupe visitors for CRO metrics.
export const getVisitorId = () => {
  let visitorId = localStorage.getItem("visitorId");
  if (!visitorId) {
    visitorId = uniqid();
    try {
      localStorage.setItem("visitorId", visitorId);
    } catch (e) {
      // Ignore storage errors (private mode etc.)
    }
  }
  return visitorId;
};

// First-touch acquisition signals for CRO channel attribution. Read the landing
// URL's UTM params (+ gclid/fbclid, which paid links carry even when untagged)
// and the referrer. Persisted per-browser so a later navigation that drops the
// query string doesn't lose the original attribution; the server classifies
// these into a channel bucket (organic/paid/social/direct/referral).
const getAttribution = () => {
  try {
    const params = new URLSearchParams(window.location.search || "");
    const referrer = document.referrer || "";
    const signals = {
      utmSource: params.get("utm_source") || "",
      utmMedium: params.get("utm_medium") || "",
      utmCampaign: params.get("utm_campaign") || "",
      // Ad-click ids imply paid even when the campaign forgot the utm tags.
      gclid: params.get("gclid") ? "1" : "",
      fbclid: params.get("fbclid") ? "1" : "",
      referrer,
    };

    // Fresh campaign tag in the URL wins over stored first-touch — otherwise a
    // known visitor clicking an email link never gets campaign-attributed.
    if (signals.utmCampaign) {
      try { localStorage.setItem("visitAttribution", JSON.stringify(signals)); } catch (e) {}
      return signals;
    }

    // First-touch persistence: keep the earliest meaningful attribution.
    const hasSignal = signals.utmSource || signals.gclid || signals.fbclid || referrer;
    const stored = localStorage.getItem("visitAttribution");
    if (!stored && hasSignal) {
      try {
        localStorage.setItem("visitAttribution", JSON.stringify(signals));
      } catch (e) {
        // Ignore storage errors (private mode etc.)
      }
      return signals;
    }
    if (stored) return JSON.parse(stored);
    return signals;
  } catch (e) {
    return {};
  }
};

// Record a visit. Fires once on app load. Fully silent — must never break the UI.
export const trackVisit = async () => {
  try {
    const visitorId = getVisitorId();
    const uid = localStorage.getItem("uniqueUserId") || "";
    const sessionId = localStorage.getItem("sessionId") || "";
    await axios.post(`${BASE_URL}/track-visit`, {
      visitorId,
      uid,
      sessionId,
      ...getAttribution(),
    });
  } catch (error) {
    // Silent by design.
  }
};

// ── Split tests ──
// Sticky deterministic bucketing: FNV-1a over visitorId+experiment, so a
// visitor always lands in the same arm across sessions/pages with no server
// round-trip (same family as the server's promoEvents.assignVariant).
const fnv1a = (str) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
};

// Pick this visitor's arm for an experiment (even split across `arms`).
// The arms array order must stay stable for the life of the experiment.
export const getAbVariant = (experiment, arms) =>
  arms[fnv1a(`${getVisitorId()}:${experiment}`) % arms.length];

// Record that a visitor was actually SHOWN an experiment arm. Deduped
// server-side per visitor/experiment/day. Silent — must never break the UI.
// Cart experiments also pass the cart `sessionId` so the server can stamp the
// arm on the cart (purchases are keyed by cart, exposures by visitor).
export const trackAbExposure = async (experiment, variant, sessionId = null) => {
  try {
    await axios.post(`${BASE_URL}/track-ab`, {
      visitorId: getVisitorId(),
      experiment,
      variant,
      ...(sessionId ? { sessionId } : {}),
    });
  } catch (error) {
    // Silent by design.
  }
};

// Record a wall-product detail view. Fires when a WallProduct page opens (?p=id).
// Deduped server-side per visitor/product/day. Silent — must never break the page.
// `extra` carries the variation dimension (e.g. { scene, bw }).
export const trackProductView = async (productId, extra = {}) => {
  try {
    if (!productId) return;
    const visitorId = getVisitorId();
    const uid = localStorage.getItem("uniqueUserId") || "";
    const sessionId = localStorage.getItem("sessionId") || "";
    await axios.post(`${BASE_URL}/track-product-view`, {
      visitorId,
      productId,
      uid,
      sessionId,
      ...extra,
      ...getAttribution(),
    });
  } catch (error) {
    // Silent by design.
  }
};

// Record the "build started" mid-funnel step (first image placed on the wall).
// Sets `built` on the same per-visitor/product/day doc as the view. Silent.
export const trackProductBuild = (productId, extra = {}) =>
  trackProductView(productId, { ...extra, built: true });
